import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import PaymentDialog from './PaymentDialog';
import { BillingKeyframes } from './BillingBanner';
import { fetchSubscriptionPlans } from './billingApi';
import { plansLoaded, plansFailed, openPay, closePay } from './billingSlice';
import { seesBilling, shouldPrompt } from './billing.rules';
import { logout, setBusiness, setActiveUser } from '../../SignupAndLogin/loginSlice';

/**
 * When the payment dialog comes up.
 *
 * Lives in the layout, so it is mounted for as long as somebody is signed in
 * and sees every tab change through one `useLocation`.
 *
 *   on sign-in        the effect below runs on mount
 *   on every tab      the same effect, because `pathname` is a dependency
 *
 * That is the whole rule, and it is the same for `loop` and `stuck`. The
 * difference between the two is entirely in whether the dialog will close —
 * which is PaymentDialog's business, not this file's.
 *
 * A waiter gets nothing, ever: this returns null before anything else
 * happens, so no plans are fetched and no dialog is built for them.
 */
export default function BillingGate() {
  const dispatch = useDispatch();
  const location = useLocation();

  const activeUser = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);
  const business = useSelector((state) => state.login.business) || activeUser?.business;

  const plans = useSelector((state) => state.billing.plans);
  const plansFetched = useSelector((state) => state.billing.plansFetched);
  const plansError = useSelector((state) => state.billing.plansError);
  const payOpen = useSelector((state) => state.billing.payOpen);

  const [loadingPlans, setLoadingPlans] = React.useState(false);

  const role = activeUser?.role;
  const visible = seesBilling(role);
  const prompt = shouldPrompt(role, business);

  /* ── the plans, once ──────────────────────────────────────────────── */

  const loadPlans = React.useCallback(async () => {
    if (!token) return;
    setLoadingPlans(true);
    try {
      const list = await fetchSubscriptionPlans({ token });
      dispatch(plansLoaded(list));
    } catch (error) {
      dispatch(plansFailed(error?.response?.data?.message || 'failed'));
    } finally {
      setLoadingPlans(false);
    }
  }, [token, dispatch]);

  React.useEffect(() => {
    if (!prompt || plansFetched || loadingPlans || plansError) return;
    loadPlans();
  }, [prompt, plansFetched, loadingPlans, plansError, loadPlans]);

  /* ── open it: on sign-in, and on every tab change ─────────────────── */

  React.useEffect(() => {
    if (prompt) dispatch(openPay());
  }, [location.pathname, prompt, dispatch]);

  /* ── the answer comes back with the business attached ─────────────── */

  /**
   * The server returns the updated business on a successful charge, with
   * `notify` already reset to `none`. Writing it straight into the session
   * is what makes the strip disappear — no re-fetch, no second call, no
   * guessing.
   *
   * Both copies have to move: screens around the app read `state.login
   * .business`, and others reach through `state.login.activeUser.business`.
   */
  const handlePaid = React.useCallback(
    (updated) => {
      if (!updated) return;
      dispatch(setBusiness(updated));
      if (activeUser) dispatch(setActiveUser({ ...activeUser, business: updated }));
    },
    [dispatch, activeUser]
  );

  /**
   * The other door out of a locked till.
   *
   * `logout` empties every slice but login (store.js), so the next person
   * signs in cleanly — including a waiter, who can then carry on serving on a
   * till whose bill is still outstanding.
   */
  const handleSignOut = React.useCallback(() => {
    dispatch(closePay());
    dispatch(logout());
  }, [dispatch]);

  if (!visible) return null;

  return (
    <>
      <BillingKeyframes />
      {payOpen ? (
        <PaymentDialog
          business={business}
          plans={plans}
          plansError={plansError}
          plansLoading={loadingPlans}
          onRetryPlans={loadPlans}
          token={token}
          onClose={() => dispatch(closePay())}
          onPaid={handlePaid}
          onSignOut={handleSignOut}
        />
      ) : null}
    </>
  );
}
